import { useState, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import {
  Branch,
  Role,
  Route,
  UserDetailResponse,
  UserNamePassword,
} from "../../types/user.type";
import {
  createUser,
  editUser,
  getAllBranch,
  getAllRole,
  getAllRoutes,
  getUser,
} from "../../services";
import {
  INITIAL_USER_FORM,
  UserForm,
  UserFormErrors,
  isSalesPerson,
  needsStaffDetails,
  toUserCreateRequest,
  toUserForm,
  toUserUpdateRequest,
  validateUserForm,
} from "./userForm";
import SalesStaffFields, { UserTextChangeEvent } from "./SalesStaffFields";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

const DRAWER_WIDTH = 600;

interface UserDrawerProps {
  userId?: string;
  open: boolean;
  onClose: () => void;
  setOpenModalPassword?: Dispatch<SetStateAction<boolean>>;
  setUsernamePassword?: Dispatch<SetStateAction<UserNamePassword>>;
  setTriggerFetch?: Dispatch<SetStateAction<number>>;
}

export default function UserDrawer({
  userId,
  open,
  onClose,
  setOpenModalPassword,
  setUsernamePassword,
  setTriggerFetch,
}: UserDrawerProps) {
  const [form, setForm] = useState<UserForm>(INITIAL_USER_FORM);
  // A new account never has a staff row yet; an edited one reports it when loaded.
  const [hasStaff, setHasStaff] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);

  const isEditing = Boolean(userId);
  const isSales = isSalesPerson(form.roles);
  const requireStaffDetails = needsStaffDetails(form, hasStaff);
  const selectedBranch = useMemo(
    () => branches.find((branch) => branch.uuid === form.branch_uuid),
    [branches, form.branch_uuid],
  );

  useEffect(() => {
    const onGetAllRole = async () => {
      const {
        data: { data },
      } = await getAllRole();
      setRoles(data);
    };

    onGetAllRole();
  }, []);

  useEffect(() => {
    const onGetAllBranch = async () => {
      const {
        data: { data },
      } = await getAllBranch();
      setBranches(data);
    };

    if (open) {
      onGetAllBranch();
    }
  }, [open]);

  // The edit form names its branch by uuid, so it is filled once the branch list is there to map to.
  useEffect(() => {
    const onGetUser = async (editedUserId: string) => {
      const {
        data: { data },
      } = await getUser(editedUserId);
      const user: UserDetailResponse = data;
      setForm(toUserForm(user, branches));
      setHasStaff(user.hasStaff);
    };

    if (open && userId && branches.length > 0) {
      onGetUser(userId);
    }
  }, [open, userId, branches]);

  useEffect(() => {
    const onGetRoutes = async (companyId: string) => {
      const {
        data: { data },
      } = await getAllRoutes(companyId);
      setRoutes(data);
    };

    if (open && isSales && selectedBranch) {
      onGetRoutes(selectedBranch.company_id);
    }
  }, [open, isSales, selectedBranch]);

  const clearError = (field: keyof UserForm) =>
    setErrors((prev) => ({ ...prev, [field]: "" }));

  const handleTextChange =
    (field: keyof UserForm) => (event: UserTextChangeEvent) => {
      const { value } = event.target;
      setForm((prev) => ({ ...prev, [field]: value }));
      clearError(field);
    };

  // A route belongs to one branch, so a new branch starts without one.
  const handleBranchChange = (event: UserTextChangeEvent) => {
    const branchUuid = event.target.value;
    setForm((prev) => ({ ...prev, branch_uuid: branchUuid, routeCode: "" }));
    setRoutes([]);
    clearError("branch_uuid");
  };

  const handleRoleChange = (event: UserTextChangeEvent) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, roles: value.split(",") }));
    clearError("roles");
  };

  const resetForm = () => {
    setForm(INITIAL_USER_FORM);
    setHasStaff(false);
    setRoutes([]);
    setErrors({});
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
    resetForm();
  };

  const createAccount = async () => {
    const {
      data: { data },
    } = await createUser(toUserCreateRequest(form, requireStaffDetails));
    if (!data) return;
    setOpenModalPassword?.(true);
    setUsernamePassword?.({ username: data.username, password: data.password });
  };

  const saveUser = async () => {
    if (userId) {
      await editUser(userId, toUserUpdateRequest(form, requireStaffDetails));
      return;
    }
    await createAccount();
  };

  const handleSave = async () => {
    const newErrors = validateUserForm(form, requireStaffDetails);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      await saveUser();
      setTriggerFetch?.((prev) => prev + 1);
      onClose();
      resetForm();
    } catch {
      // The axios interceptor reports the failure; the drawer stays open so the
      // input can be corrected and saved again.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            padding: 0,
          },
        },
      }}
    >
      <Box
        sx={{
          width: DRAWER_WIDTH,
          maxWidth: "100vw",
          p: 3,
          [compact]: {
            p: 2,
            maxHeight: "100vh",
            overflowY: "auto",
          },
        }}
      >
        <Typography variant="body1" fontWeight={500} gutterBottom>
          {isEditing ? "แก้ไขผู้ใช้งาน" : "สร้างผู้ใช้งานใหม่"}
        </Typography>
        <Divider sx={{ mb: 3, [compact]: { mb: 2 } }} />

        <Stack gap={2}>
          <TextField
            label="ชื่อผู้ใช้ (Username)"
            value={form.username}
            onChange={handleTextChange("username")}
            error={Boolean(errors.username)}
            helperText={errors.username}
            size="small"
            disabled={isEditing}
          />
          <TextField
            label="ชื่อจริง"
            value={form.firstName}
            onChange={handleTextChange("firstName")}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
            size="small"
          />
          <TextField
            label="นามสกุล"
            value={form.lastName}
            onChange={handleTextChange("lastName")}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
            size="small"
          />
          <TextField
            label="อีเมล์"
            type="email"
            value={form.email}
            onChange={handleTextChange("email")}
            error={Boolean(errors.email)}
            helperText={errors.email}
            size="small"
          />
          <TextField
            select
            label="สาขา"
            value={form.branch_uuid}
            onChange={handleBranchChange}
            error={Boolean(errors.branch_uuid)}
            helperText={errors.branch_uuid}
            size="small"
          >
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.uuid}>
                {branch.branch_code} {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="ตำแหน่ง"
            value={form.roles}
            onChange={handleRoleChange}
            error={Boolean(errors.roles)}
            helperText={errors.roles}
            sx={{ textTransform: "capitalize" }}
            size="small"
          >
            {roles.map((role) => (
              <MenuItem
                key={role.id}
                value={role.name}
                sx={{ textTransform: "capitalize" }}
              >
                {role.name}
              </MenuItem>
            ))}
          </TextField>
          {isSales && (
            <TextField
              select
              label="สาย (Route Code)"
              value={form.routeCode}
              onChange={handleTextChange("routeCode")}
              error={Boolean(errors.routeCode)}
              helperText={errors.routeCode || "ต้องเป็นสายของสาขาที่เลือก"}
              disabled={!selectedBranch}
              size="small"
            >
              {routes.map((route) => (
                <MenuItem key={route.uuid} value={route.route_code}>
                  {route.route_code} {route.route_name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {requireStaffDetails && (
            <SalesStaffFields
              form={form}
              errors={errors}
              onTextChange={handleTextChange}
            />
          )}
          {!isEditing && (
            <Alert severity="info">
              ระบบจะสร้างรหัสผ่านชั่วคราวให้
              ผู้ใช้ต้องเปลี่ยนรหัสผ่านเมื่อเข้าใช้งานครั้งแรก
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2}>
            <Button disabled={loading} onClick={handleClose}>
              ยกเลิก
            </Button>
            <Button loading={loading} variant="contained" onClick={handleSave}>
              บันทึก
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
